import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useMemo, useEffect } from 'react';
import { Download, Calendar, Loader2, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { fetchPFInterestEntries } from '../../services/pfInterestApi';

const FdInterestReportView = () => {
  const { hasPermission } = useAuth();
  const currentYear = new Date().getFullYear();
  
  // Helper to get 2-digit year
  const getShortYear = (year) => year.toString().slice(-2);
  
  const currentMonth = new Date().getMonth(); // 0-indexed (Jan = 0, Apr = 3)
  const currentFyStart = currentMonth >= 3 ? currentYear : currentYear - 1;

  // Default to current financial year
  const initialFy = `${currentFyStart}-${getShortYear(currentFyStart + 1)}`;
  
  const [financialYear, setFinancialYear] = useState(initialFy);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({ amount: 0, maturityVal: 0, interestReceivable: 0, interestRecv: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generate options for financial years starting from 2025-26 up to current FY
  const fyOptions = useMemo(() => {
    const startYr = 2025;
    const endYr = Math.max(2025, currentFyStart);
    const options = [];
    for (let i = startYr; i <= endYr; i++) {
      options.push(`${i}-${getShortYear(i + 1)}`);
    }
    return options;
  }, [currentFyStart]);

  // Derive start and end years based on selected financial year
  const startYear = parseInt(financialYear.split('-')[0]);
  const endYear = startYear + 1;

  const title = `INTEREST RECEIVED ON ACCOUNT OF PROVIDENT FUND FOR THE PERIOD -1-04-${startYear.toString().slice(-2)} TO 31-03-${endYear}`;

  useEffect(() => {
    const controller = new AbortController();
    
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchPFInterestEntries(financialYear, controller.signal);
        setData(response.entries || []);
        setTotals({
          amount: response.totals?.total_amount_of_fd || 0,
          maturityVal: response.totals?.total_maturity_value || 0,
          interestReceivable: Math.round(response.totals?.total_interest_receivable || 0),
          interestRecv: Math.round(response.totals?.total_interest_received || 0),
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Failed to load data');
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
    return () => controller.abort();
  }, [financialYear]);

  const handleExport = () => {
    if (data.length === 0) return;

    const titleRow = [[title]];
    const headers = [
      'SR.NO', 'NAME OF THE BANK', 'DATE OF INVESTMENT', 'F.D. NO',
      'AMOUNT OF F.D', 'RATE OF INTEREST', 'DURATION', 'DATE OF MATURITY',
      'MONTH', 'MATURITY VALUE', 'INTEREST AMOUNT', 'NO OF DAYS',
      'INTEREST RECEIVABLE', 'INTEREST RECEIVED', 'REMARK'
    ];
    
    const dataRows = data.map(row => [
      row.sr_no, row.bank_name, row.date_of_investment, row.fd_no, row.amount_of_fd, row.rate_of_interest,
      row.duration, row.date_of_maturity, row.month, row.maturity_value, row.interest_amount,
      row.no_of_days, row.interest_receivable || 0, row.interest_received, row.remark
    ]);

    const totalRow = [
      '', 'TOTAL', '', '', totals.amount, '', '', '', '',
      totals.maturityVal, '', '', totals.interestReceivable, totals.interestRecv, ''
    ];

    const sheetData = [
      titleRow[0],
      [], // Empty row
      headers,
      ...dataRows,
      totalRow
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    
    // Merge cells for title
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 14 } }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'FD Interest');
    
    XLSX.writeFile(workbook, `FD_Interest_${financialYear}.xlsx`);
  };

  const getThClass = () => `border border-gray-400 px-2 py-3 text-xs font-semibold text-center whitespace-nowrap bg-white text-gray-800 uppercase`;
  const getTdClass = (isNumeric = false, isCenter = false) => `border border-gray-400 px-2 py-2 text-xs ${isNumeric ? 'text-right' : (isCenter ? 'text-center' : 'text-left')} text-gray-800 bg-white`;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Filters and Actions */}
      <div className="p-4 border-b border-gray-200 flex flex-wrap gap-4 items-end justify-between bg-gray-50">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Financial Year
            </label>
            <select
              value={financialYear}
              onChange={(e) => setFinancialYear(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[140px]"
            >
              {fyOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div>
          {hasPermission('report:export') && (
<button
            onClick={handleExport}
            disabled={loading || data.length === 0}
            className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </button>
)}
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-auto p-4 flex justify-center bg-gray-50">
        <div className="inline-block align-middle pb-10 w-full">
          <div className="bg-white p-6 shadow-sm border border-gray-300 overflow-x-auto min-h-[400px] relative">
            
            {loading && (
              <div className="absolute inset-0 bg-white/80 z-10 flex flex-col items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-2" />
                <p className="text-sm font-medium text-gray-600">Loading records...</p>
              </div>
            )}

            {error && (
              <div className="absolute inset-0 bg-white z-10 flex flex-col items-center justify-center text-red-500">
                <AlertCircle className="w-8 h-8 mb-2" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            {!loading && !error && data.length === 0 && (
              <div className="absolute inset-0 bg-white z-10 flex flex-col items-center justify-center text-gray-500">
                <p className="text-sm font-medium">No records found for {financialYear}</p>
              </div>
            )}

            <table className="w-full border-collapse border border-gray-400">
              <thead>
                <tr>
                  <th colSpan={15} className="border border-gray-400 px-4 py-2 text-sm text-center font-normal uppercase bg-white">
                    {title}
                  </th>
                </tr>
                <tr>
                  <th className={getThClass()}>SR.NO</th>
                  <th className={getThClass()}>NAME OF THE BANK</th>
                  <th className={getThClass()}>DATE OF<br/>INVESTMENT</th>
                  <th className={getThClass()}>F.D. NO</th>
                  <th className={getThClass()}>AMOUNT OF<br/>F.D</th>
                  <th className={getThClass()}>RATE OF<br/>INTEREST</th>
                  <th className={getThClass()}>DURATION</th>
                  <th className={getThClass()}>DATE OF<br/>MATURITY</th>
                  <th className={getThClass()}>MONTH</th>
                  <th className={getThClass()}>MATURITY<br/>VALUE</th>
                  <th className={getThClass()}>INTEREST<br/>AMOUNT</th>
                  <th className={getThClass()}>NO OF<br/>DAYS</th>
                  <th className={getThClass()}>INTEREST<br/>RECEIVABLE</th>
                  <th className={getThClass()}>INTEREST<br/>RECEIVED</th>
                  <th className={getThClass()}>REMARK</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className={getTdClass(false, true)}>{row.sr_no}</td>
                    <td className={getTdClass()}>{row.bank_name}</td>
                    <td className={getTdClass(false, true)}>{row.date_of_investment}</td>
                    <td className={getTdClass(false, true)}>{row.fd_no}</td>
                    <td className={getTdClass(true)}>{row.amount_of_fd}</td>
                    <td className={getTdClass(true)}>{row.rate_of_interest}</td>
                    <td className={getTdClass(false, true)}>{row.duration}</td>
                    <td className={getTdClass(false, true)}>{row.date_of_maturity}</td>
                    <td className={getTdClass(false, true)}>{row.month}</td>
                    <td className={getTdClass(true)}>{row.maturity_value}</td>
                    <td className={getTdClass(true)}>{row.interest_amount}</td>
                    <td className={getTdClass(true)}>{row.no_of_days}</td>
                    <td className={getTdClass(true)}>{row.interest_receivable && parseFloat(row.interest_receivable) > 0 ? row.interest_receivable : '—'}</td>
                    <td className={getTdClass(true)}>{row.interest_received}</td>
                    <td className={getTdClass()}>
                      {row.remark && String(row.remark).split(' ').map((word, i) => (
                        <React.Fragment key={i}>
                          {word} {i === 1 ? <br/> : ''}
                        </React.Fragment>
                      ))}
                    </td>
                  </tr>
                ))}
                
                {/* Total Row */}
                {data.length > 0 && (
                  <tr>
                    <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                    <td className="border border-gray-400 px-3 py-2 text-sm text-left bg-white text-gray-800">TOTAL</td>
                    <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                    <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                    <td className="border border-gray-400 px-3 py-2 text-xs text-right bg-white text-gray-800">{totals.amount}</td>
                    <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                    <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                    <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                    <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                    <td className="border border-gray-400 px-3 py-2 text-xs text-right bg-white text-gray-800">{totals.maturityVal}</td>
                    <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                    <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                    <td className="border border-gray-400 px-3 py-2 text-xs text-right bg-white text-gray-800">{totals.interestReceivable > 0 ? totals.interestReceivable : '—'}</td>
                    <td className="border border-gray-400 px-3 py-2 text-xs text-right bg-white text-gray-800">{totals.interestRecv}</td>
                    <td className="border border-gray-400 px-3 py-2 bg-white"></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FdInterestReportView;
